import React, { ReactElement } from "react";
import { useTranslation } from "next-i18next";
import QRCode from "react-qr-code";
import { trackEvent } from "@kunduz/utility-analytics";
import { copyToClipboard } from "../../services/utilityService";
import styles from "./questionSideCta.module.scss";
import { QuestionIndexing } from "@kunduz/data-access-qi-api";
import { useStudentLimits } from "@kunduz/data-access-core-api";
import { useAppContext } from "@kunduz/feature-website-context";
import { Button, Text } from "@kunduz/ui-atoms";
import { Copy01, Link01 } from "@untitled-ui/icons-react";

const ReferFriendPaidCta = (props: Props): ReactElement => {
  const { t } = useTranslation("questionIndexing");
  const { student, kunduzApiToken } = useAppContext();
  const { studentLimits } = useStudentLimits(kunduzApiToken);

  function copyReferralCode(e) {
    e.preventDefault();
    copyToClipboard(student.data?.referral_code?.code);
    trackCopyClick();
  }

  function copyLink(e) {
    e.preventDefault();
    copyToClipboard(student.data?.referral_code?.shareable_link);
    trackCopyClick();
  }

  function trackCopyClick() {
    const plan_type = !student.data?.primary_package
      ? "freemium"
      : student.data?.primary_package?.package.is_recurring
        ? "monthly"
        : "unlimited";

    trackEvent("Question Indexing Referral Information Copied", {
      course: props.question?.course?.slug,
      subject: props.question?.subject?.slug,
      page_slug: window.location.pathname,
      remaining_answer_credit: studentLimits?.questions?.remained,
      initial_answer_credit: studentLimits?.questions?.total,
      plan_type,
      persona: undefined,
    });
  }

  return (
    <div className={styles.referFriendCta}>
      <GiftIcon />
      <div
        className={styles.title}
        dangerouslySetInnerHTML={{
          __html: t(
            "question_indexing_sidebar_component_referral_paid_user_state_primary_proposition_text",
            {
              reward:
                student.data?.referral_code?.referral_campaign
                  ?.owner_discount_value,
            },
          ),
        }}
      ></div>

      <p className={styles.desc}>
        {t(
          "question_indexing_sidebar_component_referral_paid_user_state_secondary_proposition_text",
          {
            reward:
              student.data?.referral_code?.referral_campaign
                ?.owner_discount_value,
          },
        )}
      </p>

      <div className={styles.qrLine}>
        <div className={styles.qrWrapper}>
          <QRCode value={student.data?.referral_code?.code || ""} size={106} />
        </div>
        <Arrow className={styles.stupidArrow} />
        <span>
          {t(
            "question_indexing_sidebar_component_referral_paid_user_state_arrow_text",
          )}
        </span>
      </div>

      <div className={styles.buttonWrapper}>
        <Button
          variant="secondary"
          color="gray"
          size={"xl"}
          onClick={copyReferralCode}
        >
          <Copy01 />
          <Text variant="textmd" weight="semibold">
            {student.data?.referral_code?.code}
          </Text>
        </Button>

        <Button variant="secondary" color="gray" size={"xl"} onClick={copyLink}>
          <Link01 />
          <Text variant="textmd" weight="semibold">
            {t(
              "question_indexing_sidebar_component_referral_state_copylink_cta_label",
            )}
          </Text>
        </Button>
      </div>
    </div>
  );
};

interface Props {
  question?: QuestionIndexing.TrQiQuestion;
}

// TODO turn into standalone svgs
export function Arrow(props) {
  return (
    <svg
      width="64"
      height="35"
      viewBox="0 0 64 35"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={props.className}
    >
      <path
        d="M35.0316 32.6074C35.37 32.5278 35.6739 32.586 35.9884 32.7028C38.6118 33.6783 41.3188 34.1486 44.1216 34.1103C47.4412 34.0649 50.709 33.7576 53.8731 32.6492C55.5336 32.0681 57.1656 31.4327 58.7299 30.6279C58.9078 30.5364 59.0993 30.3389 59.2988 30.4834C59.5545 30.6679 59.3496 30.9202 59.2988 31.1355C59.1685 31.6853 58.9849 32.2237 58.8871 32.7793C58.8135 33.1977 59.0933 33.6515 59.3957 33.7274C59.8094 33.8321 59.9719 33.5129 60.155 33.2435C60.2389 33.1213 60.3211 32.9958 60.3933 32.8664C61.2059 31.4123 62.0107 29.9538 62.8308 28.5029C63.137 27.9599 63.1853 27.4272 62.8194 26.9086C62.4611 26.4003 61.9401 26.365 61.3775 26.4676C59.4155 26.8225 57.4516 27.1672 55.4882 27.5212C55.2254 27.5685 54.9621 27.6323 54.7135 27.726C54.4541 27.824 54.2312 27.9958 54.2499 28.3126C54.2689 28.6375 54.5192 28.7601 54.7959 28.8092C55.0582 28.8562 55.3293 28.8551 55.593 28.8972C55.8745 28.9408 56.1814 28.854 56.4565 29.0431C56.3254 29.3427 56.0438 29.376 55.8108 29.4786C52.641 30.8721 49.316 31.6112 45.8652 31.7815C43.7209 31.8875 41.5793 31.8858 39.4748 31.3605C39.2918 31.3143 39.0367 31.3591 38.9909 31.0274C39.8709 30.4379 40.7222 29.793 41.5274 29.0855C46.1437 25.0267 45.605 18.247 41.5983 14.8043C40.7592 14.0834 39.9041 13.3771 38.924 12.8749C36.3663 11.5661 33.6694 11.2745 30.9655 12.2685C25.8367 14.156 24.7639 20.2191 26.0237 23.6482C26.9735 26.2366 28.7152 28.2502 30.8829 29.9146C31.229 30.1806 31.6427 30.3633 31.9608 30.7391C31.742 30.9206 31.5883 30.9145 31.4428 30.921C29.2544 31.0151 27.097 30.8032 24.999 30.1649C20.284 28.7292 16.1095 26.2995 12.4081 23.0777C9.97055 20.9566 7.96698 18.4266 6.18123 15.7421C3.85911 12.2461 2.26247 8.45969 1.81829 4.24549C1.71066 3.22858 1.67906 2.22009 2.26955 1.31021C2.38255 1.13614 2.55544 0.945452 2.3377 0.764635C2.10359 0.570469 1.89199 0.740191 1.71084 0.904424C1.50753 1.08779 1.38387 1.32549 1.2682 1.56875C0.995635 2.14302 0.868041 2.75288 0.847731 3.38298C0.754226 6.28753 1.31982 9.08257 2.46267 11.734C5.00894 17.636 8.73528 22.6756 13.8332 26.6167C17.4689 29.4288 21.4645 31.5892 26.0082 32.5726C29.0085 33.2229 32.022 33.32 35.0316 32.6074ZM35.0643 29.7791C32.5334 28.4899 30.3369 26.8342 28.7527 24.4149C27.0448 21.8072 27.2849 17.9913 29.3793 15.7158C30.5526 14.4411 32.044 13.8265 33.7424 13.7788C35.5984 13.7271 37.3388 14.1574 38.8574 15.3019C40.054 16.204 41.1656 17.1773 41.8529 18.5484C42.1494 19.1401 42.3306 19.7641 42.404 20.3133C42.9128 23.0068 42.2517 25.2428 40.3693 27.1052C39.2378 28.2244 37.9695 29.14 36.5127 29.7916C36.0214 30.0117 35.5654 30.0334 35.0643 29.7791Z"
        fill="#205EB8"
      />
    </svg>
  );
}

export function GiftIcon() {
  return (
    <svg
      width="27"
      height="34"
      viewBox="0 0 27 34"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_645_9817)">
        <path
          d="M1.125 12.9292H25.875V30.3961C25.875 32.2485 24.3733 33.7501 22.5209 33.7501H4.47904C2.62666 33.7501 1.125 32.2485 1.125 30.3961V12.9292Z"
          fill="#205EB8"
        />
        <path
          d="M24.1863 8.99023H2.81364C1.25971 8.99023 0 10.2499 0 11.8039V11.8039C0 13.3578 1.25971 14.6175 2.81364 14.6175H24.1863C25.7402 14.6175 27 13.3578 27 11.8039C27 10.2499 25.7402 8.99023 24.1863 8.99023Z"
          fill="#01CCBC"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M13.0822 6.10754C12.9744 5.84531 12.8559 5.58428 12.7295 5.32753C12.1953 4.2437 11.4695 3.14036 10.6403 2.22603C9.82783 1.33018 8.82917 0.519654 7.72757 0.174858C7.16071 -0.00257358 6.54381 -0.0630667 5.91683 0.0786048C5.2847 0.221439 4.71531 0.55349 4.22634 1.04754C3.73244 1.54657 3.40204 2.13926 3.32576 2.81389C3.25031 3.48105 3.43903 4.08995 3.72788 4.60087C4.27909 5.57584 5.30938 6.39828 6.3268 7.04782C8.16989 8.22439 10.4914 9.1531 11.7875 9.5716V33.7498H14.875V9.40314C16.2131 8.93107 18.2114 8.08597 19.8373 7.04792C20.8548 6.39837 21.8851 5.57589 22.4363 4.6009C22.7252 4.08997 22.9139 3.48106 22.8385 2.8139C22.7622 2.13927 22.4319 1.54658 21.938 1.04757C21.449 0.553529 20.8797 0.221495 20.2475 0.0786809C19.6205 -0.0629717 19.0036 -0.00245893 18.4368 0.174992C17.3352 0.519822 16.3365 1.33039 15.524 2.22627C14.6948 3.14063 13.9689 4.244 13.4347 5.32787C13.3083 5.58447 13.1899 5.84545 13.0822 6.10754ZM5.87185 2.6774C6.08674 2.46026 6.27362 2.37289 6.42689 2.33827C6.58523 2.30248 6.78276 2.3065 7.03598 2.38576C7.57463 2.55435 8.23893 3.02595 8.92521 3.78267C9.59472 4.52093 10.2033 5.44064 10.6524 6.35199C10.7188 6.4867 10.7808 6.61929 10.8383 6.74918C9.79698 6.31877 8.59003 5.74444 7.57256 5.09495C6.6279 4.49187 6.00401 3.92107 5.7435 3.46031C5.62654 3.25337 5.61998 3.1338 5.62673 3.07416C5.63266 3.02198 5.66188 2.88954 5.87185 2.6774ZM19.7374 2.33837C19.8906 2.37299 20.0776 2.46036 20.2924 2.67748C20.5024 2.88963 20.5316 3.02206 20.5375 3.07424C20.5442 3.13389 20.5377 3.25345 20.4207 3.4604C20.1602 3.92117 19.5363 4.49199 18.5916 5.09509C17.5741 5.74464 16.3672 6.31901 15.3259 6.74942C15.3834 6.61953 15.4453 6.48699 15.5117 6.35223C15.9609 5.44084 16.5695 4.52114 17.239 3.78286C17.9253 3.02611 18.5896 2.55449 19.1283 2.38588C19.3815 2.30661 19.579 2.30259 19.7374 2.33837Z"
          fill="#2B81FC"
        />
      </g>
      <defs>
        <clipPath id="clip0_645_9817">
          <rect width="27" height="33.75" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
}

export default ReferFriendPaidCta;
